import {
  BillCountSchema,
  type BillRange,
  type DecideStatementGroup,
  type PutWalkIn,
  type StatementMove,
  StatementViewSchema,
  WalkInResponseSchema,
} from '@oneapp/shared';
import { api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
const enc = encodeURIComponent;

/** No records (Phase 16; see packages/shared/src/api.ts): a statement's money in, and walk-in sales. */
export const recordsApi = {
  statement: (importId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/imports/${enc(importId)}/statement`, StatementViewSchema, { auth: true, ...withSignal(signal) }),

  decide: (importId: string, groupId: string, body: DecideStatementGroup) =>
    api.post(`/api/v1/imports/${enc(importId)}/statement-groups/${enc(groupId)}/confirm`, StatementViewSchema, {
      auth: true,
      body,
    }),

  move: (importId: string, entryId: string, group: StatementMove) =>
    api.patch(`/api/v1/imports/${enc(importId)}/statement-entries/${enc(entryId)}`, StatementViewSchema, {
      auth: true,
      body: { group },
    }),

  walkIn: (returnId: string, signal?: AbortSignal) =>
    api.get(`/api/v1/returns/${enc(returnId)}/walk-in`, WalkInResponseSchema, { auth: true, ...withSignal(signal) }),

  saveWalkIn: (returnId: string, body: PutWalkIn) =>
    api.put(`/api/v1/returns/${enc(returnId)}/walk-in`, WalkInResponseSchema, { auth: true, body }),

  removeWalkIn: (returnId: string) =>
    api.delete(`/api/v1/returns/${enc(returnId)}/walk-in`, WalkInResponseSchema, { auth: true }),

  countBills: (returnId: string, bills: BillRange[], signal?: AbortSignal) =>
    api.post(`/api/v1/returns/${enc(returnId)}/walk-in/bills`, BillCountSchema, {
      auth: true,
      body: { bills },
      ...withSignal(signal),
    }),
};

export const recordsKeys = {
  statement: (importId: string) => ['statement', importId] as const,
  walkIn: (returnId: string) => ['walk-in', returnId] as const,
  bills: (returnId: string, from: string, to: string) => ['walk-in', returnId, 'bills', from, to] as const,
};

/** "/imports/{id}/statement": every payment in a statement (R7). */
export function parseStatementPath(path: string): { importId: string } | null {
  const m = /^\/imports\/([\w-]{1,64})\/statement\/?$/.exec(path);
  return m ? { importId: m[1] as string } : null;
}

/** What the chat worked out to start the walk-in page from: what customers paid, the rate, the bills. */
export interface WalkInStart {
  paid: number | null;
  rate: number | null;
  from: string;
  to: string;
}

/** "/returns/{id}/walk-in?paid=…&rate=…&from=…&to=…": walk-in sales by rate (R11). */
export function parseWalkInPath(path: string): ({ returnId: string } & WalkInStart) | null {
  const m = /^\/returns\/([\w-]{1,64})\/walk-in\/?(?:\?(.*))?$/.exec(path);
  if (!m) return null;
  return { returnId: m[1] as string, ...walkInStartOf(new URLSearchParams(m[2] ?? '')) };
}

export function walkInStartOf(query: URLSearchParams): WalkInStart {
  const number = (name: string) => {
    const value = Number(query.get(name));
    return Number.isFinite(value) && value > 0 ? value : null;
  };
  return { paid: number('paid'), rate: number('rate'), from: query.get('from') ?? '', to: query.get('to') ?? '' };
}
